import { TicketStatus } from "interfaces/entities/ticket.model";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck } from "react-icons/fa";

// Bridging the design's status set (open / pending / progress / resolved / closed)
// to the app's TicketStatus enum (NEW / IN_PROGRESS / WAITING_FOR_RESPONSE / RESOLVED / CLOSED).
// We keep both in step so the visual mapping is obvious and one-way.
export type DesignStatus = "open" | "pending" | "progress" | "resolved" | "closed";
export type DesignPriority = "urgent" | "high" | "normal" | "low";

export const priorityLabel: Record<DesignPriority, string> = {
  urgent: "Срочный",
  high: "Высокий",
  normal: "Обычный",
  low: "Низкий",
};

export const ALL_DESIGN_STATUSES: DesignStatus[] = [
  "open",
  "progress",
  "pending",
  "resolved",
  "closed",
];

// Backend stores TicketPriority as LOW / MEDIUM / HIGH only. We expose the
// three matching slots; "urgent" lives in DesignPriority for future use but
// is excluded from pickers until the backend gains a fourth tier.
export const ALL_DESIGN_PRIORITIES: DesignPriority[] = ["high", "normal", "low"];

export const StatusPill = ({ status }: { status: string }) => {
  const { t } = useTranslation();

  return (
    <span className={`tv2-pill tv2-pill-${status}`}>
      <span className="tv2-pill-dot" />
      {t(`ticket.status.${status}`)}
    </span>
  );
};

export const PriorityPill = ({ priority }: { priority: DesignPriority }) => (
  <span className={`tv2-pill tv2-pri-${priority}`}>
    <span className="tv2-pill-dot" />
    {priorityLabel[priority]}
  </span>
);

const colorFromString = (input: string): string => {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = (h * 31 + input.charCodeAt(i)) >>> 0;
  }
  const hue = h % 360;
  return `hsl(${hue}, 55%, 55%)`;
};

interface AvatarProps {
  name: string;
  size?: "sm" | "md" | "lg" | "xl";
  seed?: string;
}

export const Avatar = ({ name, size = "md", seed }: AvatarProps) => {
  const initials =
    (name || "?")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0] || "")
      .join("")
      .toUpperCase() || "?";
  return (
    <span
      className={`tv2-avatar ${size}`}
      style={{ background: colorFromString(seed || name || "x") }}
      aria-label={name}
    >
      {initials}
    </span>
  );
};

interface PopoverProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}

// Anchored inline relative to its `.tv2-prop-row` parent (which is
// position: relative). We close on Escape and on outside click via a
// fixed-position backdrop element.
export const Popover = ({ open, onClose, children }: PopoverProps) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <>
      <div className="tv2-popover-backdrop" onClick={onClose} />
      <div className="tv2-popover" role="dialog">
        {children}
      </div>
    </>
  );
};

interface PopoverItemProps {
  selected?: boolean;
  onClick?: () => void;
  children: ReactNode;
}

export const PopoverItem = ({ selected, onClick, children }: PopoverItemProps) => (
  <button
    type="button"
    className={`tv2-popover-item${selected ? " selected" : ""}`}
    onClick={onClick}
  >
    {children}
    {selected && (
      <span className="tv2-popover-check">
        <FaCheck size={11} />
      </span>
    )}
  </button>
);

export interface ToastEntry {
  id: string;
  text: string;
}

interface ToastsProps {
  toasts: ToastEntry[];
}

export const Toasts = ({ toasts }: ToastsProps) => (
  <div className="tv2-toast-wrap" aria-live="polite">
    {toasts.map((t) => (
      <div key={t.id} className="tv2-toast">
        <span className="tv2-toast-icon">
          <FaCheck size={12} />
        </span>
        <span>{t.text}</span>
      </div>
    ))}
  </div>
);

// Tiny helper hook for managing toasts inside the page.
export const useToasts = () => {
  const idRef = useRef(0);
  const [list, setList] = useState<ToastEntry[]>([]);
  const push = (text: string) => {
    const id = String(++idRef.current);
    setList((prev) => [...prev, { id, text }]);
    // Auto-dismiss after the design's 2.4s.
    setTimeout(() => setList((prev) => prev.filter((t) => t.id !== id)), 2400);
  };
  return { toasts: list, push };
};
